import { useCallback, useEffect, useMemo, type JSX } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RecipientSubTypeEnum } from "../../../../common/enums/RecipientSubTypeEnum";
import { RecipientTypeEnum } from "../../../../common/enums/RecipientTypeEnum";
import Form from "../../../../components/forms/form/Form";
import SaveAsDialog, { bindSaveAsDialog, useSaveAsDialogState } from "../../../../components/save-as-dialog/SaveAsDialog";
import StepMenu, { StepDefinition } from "../../../../components/step-menu/StepMenu";
import { StepMenuEditModeEnum, useStepMenuState } from "../../../../components/step-menu/StepMenuState";
import UnsavedChangesPrompt from "../../../../components/unsaved-changes-prompt/UnsavedChangesPrompt";
import { useConfirmation } from "../../../../contexts/confirmation-context/ConfirmationContext";
import { useRouteContext } from "../../../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../../../queries/UseQueryNotifications";
import { useCheckUserExists } from "../../../../queries/user-role-administration/UseCheckUserExists";
import { useSaveUser } from "../../../../queries/user-role-administration/UseSaveUser";
import { useUser } from "../../../../queries/user-role-administration/UseUser";
import i18n from "../../../../services/localization-service/i18n";
import { IEditUserDTO } from "../../../../services/user-role-administration-service/Models";
import { alarmNotificationsValid, generalIsValid, membershipIsValid, mergeUsers, NewUserID, pageVisibilityIsValid, securityIsValid, toUserField, userFieldIsDirty } from "./UserEditorUtils";
import { UICustomizationModeTypeEnum, UserAssociatedRoleFieldType, UserFieldType } from "./UserFieldTypes";
import AlarmNotificationsStep from "./alarm-notifications-step/AlarmNotificationsStep";
import GeneralStep from "./general-step/GeneralStep";
import MembershipStep from "./membership-step/MembershipStep";
import SecurityStep from "./security-step/SecurityStep";
import LoadingZone from "../../../../components/loading-zone/LoadingZone";
import { UserRecipientFieldType } from "../../../../components/user-recipient-list/UserRecipientFieldTypes";
import { List, ListItem, ListItemIcon, ListItemText, Stack, Typography, useTheme } from "@mui/material";
import { Phone, Sms } from "@mui/icons-material";
import { FeatureConstants } from "../../../../services/features-data-service/Models";
import { useEvaluateFeature } from "../../../../queries/features/UseEvaluateFeature";
import { StepEnum } from "./StepEnum";
import PageVisibilityStep from "./page-visibility-step/PageVisibilityStep";

export type UserEditorCloseType = {
    reason: "cancel" | "saveAsNew" | "saveChanges";
    userID: number | undefined;
};

export type UserEditorProps = {
    userID: number | undefined;
    organizationID: number | undefined;
    allowSSO?: boolean; // Optional, used to determine if SSO is allowed for the user
    onClose: ({ reason, userID }: UserEditorCloseType) => void;
};

export default function UserEditor({
    userID,
    organizationID,
    allowSSO,
    onClose,
}: UserEditorProps): JSX.Element {

    const { confirm, acknowledge } = useConfirmation();
    const { t } = useTranslation();
    const routeContext = useRouteContext();
    const saveAsDialog = useSaveAsDialogState();
    const theme = useTheme();

    const { data: userModel, status: useUserStatus } = useQueryNotifications(useUser)({
        groupID: routeContext.groupID as number,
        userID: userID as number
    });

    const { mutateAsync: saveUser } = useQueryNotifications(useSaveUser)({
        groupID: routeContext.groupID as number,
        organizationID: organizationID as number,
        onSuccess: (data) => {
            // save success            
            acknowledge(t("userEditor.saveSuccessMessage", data));
        }
    });

    const { mutateAsync: checkUserExists } = useQueryNotifications(useCheckUserExists)({
        groupID: routeContext.groupID as number
    });

    const initValue: UserFieldType = {
        id: NewUserID,
        firstName: undefined,
        lastName: undefined,
        companyName: undefined,
        jobTitle: undefined,
        jobFunction: "Other",
        phoneNumber: undefined,
        timeZoneIndex: 20,
        login: undefined,
        lastLoginTimestamp: undefined,
        sendNewUserNotification: true,
        requirePasswordChange: false,
        disabled: false,
        mobileAccessEnabled: false,
        multipleCustomers: false,
        isElynxAdmin: false,
        recipients: new Array<UserRecipientFieldType>(),
        associatedRoles: new Array<UserAssociatedRoleFieldType>(),
        createEmailRecipient: false,
        createSmsRecipient: false,
        createVoiceRecipient: false,
        mfaEnabled: false,
        mfaSource: undefined,
        externalSignInName: undefined,
        allowSSO: false,
        customizationSettings: undefined,

        hasUICustomizationSettings: false,
        uiCustomizationMode: UICustomizationModeTypeEnum.None,
        uiCustomizationPresetID: undefined

    };

    const form = useForm<UserFieldType>({
        mode: "all",
        defaultValues: initValue
    });

    const {
        getValues,
        formState: {
            dirtyFields
        },
        reset,
        setValue,
        control,
        handleSubmit
    } = form;

    const editMode = userID === NewUserID
        ? StepMenuEditModeEnum.Create
        : StepMenuEditModeEnum.Edit;

    const user = useWatch({ control: control }) as UserFieldType;

    const isGeneralValid = generalIsValid(user);
    const isAlarmNotificationsValid = isGeneralValid && alarmNotificationsValid(/*user*/);
    const isMembershipValid = isAlarmNotificationsValid && membershipIsValid(/*user*/);
    const isSecurityValid = isMembershipValid && securityIsValid(/*user*/);
    const isPageVisibilityValid = isSecurityValid && pageVisibilityIsValid(user);

    const isGeneralEnabled = true;
    const isAlarmNotificationsEnabled = isGeneralValid;
    const isMembershipEnabled = isAlarmNotificationsEnabled && isAlarmNotificationsValid;
    const isSecurityEnabled = isMembershipEnabled && isMembershipValid;
    const isPageVisibilityEnabled = isSecurityEnabled && isSecurityValid;

    const { data: navigationV2Feature } = useQueryNotifications(useEvaluateFeature)({
        feature: FeatureConstants.NavigationV2,
    });

    const navigationV2 = (navigationV2Feature ?? false);

    const stepDefinitions: Array<StepDefinition> = useMemo(() => {

        return [{
            key: StepEnum.General,
            title: i18n.t("userEditor.steps.general.title"),
            disabled: !isGeneralEnabled,
            valid: isGeneralValid
        }, {
            key: StepEnum.AlarmNotifications,
            title: i18n.t("userEditor.steps.alarmNotifications.title"),
            disabled: !isAlarmNotificationsEnabled,
            valid: isAlarmNotificationsValid
        }, {
            key: StepEnum.Membership,
            title: i18n.t("userEditor.steps.membership.title"),
            disabled: !isMembershipEnabled,
            valid: isMembershipValid
        }, {
            key: StepEnum.Security,
            title: i18n.t("userEditor.steps.security.title"),
            disabled: !isSecurityEnabled,
            valid: isSecurityValid
        },
        ...(
            navigationV2
                ? [{
                    key: StepEnum.PageVisibility,
                    title: i18n.t("userEditor.steps.pageVisibility.title"),
                    disabled: !isPageVisibilityEnabled,
                    valid: isPageVisibilityValid
                }]
                : []
        )];
    }, [
        isGeneralValid,
        isAlarmNotificationsValid,
        isMembershipValid,
        isSecurityValid,
        isGeneralEnabled,
        isAlarmNotificationsEnabled,
        isMembershipEnabled,
        isSecurityEnabled,
        isPageVisibilityEnabled,
        isPageVisibilityValid,
        navigationV2
    ]);

    const {
        state: {
            landing,
            steps,
            activeStep,
            cancelButtonState,
            prevButtonState,
            nextButtonState,
            saveAsNewButtonState,
            saveChangesButtonState
        },
        setActiveStep,
        setNextButtonDisabled,
        setSaveAsNewButtonDisabled,
        setSaveAsNewButtonVisible,
        setSaveChangesButtonDisabled,
        setSaveChangesButtonVisible,
    } = useStepMenuState(
        editMode,
        undefined,
        stepDefinitions
    );

    // A separate isDirty from the one returned by useForm is implemented
    // here so we can exclude the expanded and formID fields from the data.
    const isDirty = userFieldIsDirty(user, dirtyFields);

    const updateButtonStates = useCallback(() => {

        switch (activeStep) {

            case StepEnum.General:
                setNextButtonDisabled(!(isAlarmNotificationsEnabled));
                break;

            case StepEnum.AlarmNotifications:
                setNextButtonDisabled(!(isMembershipEnabled));
                break;

            case StepEnum.Membership:
                setNextButtonDisabled(!(isSecurityEnabled));
                break;

            case StepEnum.Security:
                if (navigationV2) {
                    setNextButtonDisabled(!(isPageVisibilityEnabled));
                } else {
                    setNextButtonDisabled(false);

                    // Override the step menu state default button changes
                    if (editMode) {
                        setSaveAsNewButtonVisible(false);
                        setSaveChangesButtonVisible(true);
                        setSaveChangesButtonDisabled(!isDirty);
                    } else {
                        setSaveChangesButtonVisible(false);
                        setSaveAsNewButtonVisible(true);
                        setSaveAsNewButtonDisabled(!isDirty);
                    }
                }
                break;

            case StepEnum.PageVisibility:
                setNextButtonDisabled(false);

                // Override the step menu state default button changes
                if (editMode) {
                    setSaveAsNewButtonVisible(false);
                    setSaveChangesButtonVisible(true);
                    setSaveChangesButtonDisabled(!(isPageVisibilityValid && isDirty));
                } else {
                    setSaveChangesButtonVisible(false);
                    setSaveAsNewButtonVisible(true);
                    setSaveAsNewButtonDisabled(!(isPageVisibilityValid && isDirty));
                }
                break;
        }
    }, [
        editMode,
        setNextButtonDisabled,
        setSaveAsNewButtonDisabled,
        setSaveChangesButtonDisabled,
        setSaveAsNewButtonVisible,
        setSaveChangesButtonVisible,
        activeStep,
        isDirty,
        isAlarmNotificationsEnabled,
        isMembershipEnabled,
        isSecurityEnabled,
        isPageVisibilityEnabled,
        isPageVisibilityValid,
        navigationV2
    ]);

    // #region Effects

    // This effect updates the form values after data is retrieved. Want to keep this
    // simple with minimal dependencies, otherwise rendering problems may ensue!
    useEffect(() => {
        if (userModel) {
            // We have our user now, so we need to convert it to
            // values for react hook form.
            const user: UserFieldType = toUserField(userModel, allowSSO ?? false);

            // Reset the form with the user's values
            reset(user);
        }
    }, [
        userModel,
        allowSSO,
        reset
    ]);

    // This effect updates step menu button states whenever the user form values change
    useEffect(() => {

        updateButtonStates();
    }, [user, updateButtonStates]);

    // #endregion Effects

    // #region Event Handlers

    async function onCancelEdit() {

        if (!isDirty || (await getUserExitConfirmation())) {
            onClose({ reason: "cancel", userID: undefined });
        }
    }

    function onCancelStep() {
    }

    async function onStepChange(fromStep: number, toStep: number) {

        if (fromStep === StepEnum.General) {

            if (user.login) {

                // If the user changed the login, then check to see if it's already used
                if (dirtyFields?.login) {
                    await checkUserExists({ userLogin: user.login });
                }

                // add default recipient when in add mode
                if (!editMode) {

                    const updatedRecipients = [...user.recipients];

                    if (user.createEmailRecipient) {

                        setDefaultRecipient(updatedRecipients, user.login, RecipientTypeEnum.Email);
                    } else {
                        removeRecipient(updatedRecipients, user.login, RecipientTypeEnum.Email);
                    }

                    if (user.createSmsRecipient) {
                        setDefaultRecipient(updatedRecipients, user.phoneNumber!, RecipientTypeEnum.Text);
                    } else {
                        removeRecipient(updatedRecipients, user.phoneNumber!, RecipientTypeEnum.Text);
                    }

                    if (user.createVoiceRecipient) {
                        setDefaultRecipient(updatedRecipients, user.phoneNumber!, RecipientTypeEnum.Voice);
                    } else {
                        removeRecipient(updatedRecipients, user.phoneNumber!, RecipientTypeEnum.Voice);
                    }

                    setValue("recipients", updatedRecipients);
                }


                setActiveStep(toStep);
            }
        } else {
            setActiveStep(toStep);
        }
    }

    async function onSaveAsNew() {
        const user = getValues();

        const savedUser = await save({
            ...user,
            id: NewUserID,
        });

        onClose({
            reason: "saveAsNew",
            userID: savedUser?.id
        });
    }

    const trimPhoneNumber = (value: string) => value.replace('+1', '').replace(/[-()+]/g, '').trim();

    async function onSaveChanges() {

        const user = getValues();
        const phoneNumber = trimPhoneNumber(user.phoneNumber ?? '');


        // Check if the phone number has changed and if so, check if it's used for any alarm notification recipients
        // If it is, then prompt the user to update the recipients
        if (phoneNumber !== trimPhoneNumber(userModel!.phoneNumber ?? '')) {

            const matchingRecipients = user.recipients.filter(o => o.type !== RecipientTypeEnum.Email && trimPhoneNumber(o.to) === trimPhoneNumber(userModel!.phoneNumber ?? ''));

            if (matchingRecipients.length > 0) {

                const result = await confirm({
                    dialogTitle: t("userEditor.steps.general.updateRecipients.title"),
                    yesButtonText: t("userEditor.steps.general.updateRecipients.yesButtonText"),
                    noButtonText: t("userEditor.steps.general.updateRecipients.noButtonText"),
                    showConfirmationText: false,
                    confirmationBodyComponent:
                        <Stack direction='column' spacing={2}>
                            <Typography variant="body2">
                                {t("userEditor.steps.general.updateRecipients.confirmationText1")}
                            </Typography>
                            <List dense={true}>
                                {matchingRecipients.map((o, ndx) => (
                                    <ListItem key={ndx}>
                                        <ListItemIcon sx={{ minWidth: 'auto', marginRight: theme.spacing(1) }}>
                                            {o.type === RecipientTypeEnum.Voice ? <Phone /> : <Sms />}
                                        </ListItemIcon>
                                        <ListItemText primary={o.to} />
                                    </ListItem>
                                ))}
                            </List>

                            <Typography variant="body2">
                                {t("userEditor.steps.general.updateRecipients.confirmationText2")}
                            </Typography>

                        </Stack>,
                    showYesButton: true,
                    showNoButton: true,
                    showCloseButton: true
                });

                if (result === 'close') {
                    return;
                }

                if (result === 'yes') {

                    const newRecipients = [...user.recipients];

                    matchingRecipients.forEach(m => {
                        const index = newRecipients.findIndex(o => o.to === m.to);
                        if (index !== -1) {
                            const recipient = newRecipients[index];
                            const parts = recipient.to.split(' ');
                            recipient.to = `${parts[0]} ${phoneNumber}`;

                            newRecipients[index] = recipient
                        }
                    });

                    user.recipients = newRecipients;
                }
            }
        }

        const savedUser = await save(user);

        onClose({
            reason: "saveChanges",
            userID: savedUser?.id
        });
    }

    // #endregion Event Handlers

    async function save(userField: UserFieldType): Promise<IEditUserDTO | undefined> {

        if (userModel && userField) {
            // Merge all of our changes with the original user
            const model = mergeUsers(userModel, userField);

            // Save it and return the saved user with updated ID, if applicable
            return await saveUser(model);
        }
    }

    async function getUserExitConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        })) === "yes";
    }

    // function setDefaultRecipient() {
    //     const to = user.login;

    //     if ((to?.length ?? 0) > 0) {

    //         var emailRecipient: UserRecipientFieldType = {
    //             id: 0,
    //             type: RecipientTypeEnum.Email,
    //             subType: RecipientSubTypeEnum.None,
    //             to: to as string,
    //             disabled: false,
    //             disabledTimestamp: undefined,
    //             usageCount: 0,
    //             cloneRecipientID: undefined
    //         };

    //         if (!user.recipients.find(r => r.type === emailRecipient.type &&
    //             r.subType === emailRecipient.subType &&
    //             r.to === emailRecipient.to)) {
    //             setValue("recipients", [emailRecipient]);
    //         }
    //     }
    // }

    function setDefaultRecipient(recipients: UserRecipientFieldType[], to: string, type: RecipientTypeEnum) {

        let id = recipients.length > 0 ? Math.min(...recipients.map(o => o.id)) - 1 : -1;
        if (id > 0) {
            id = -1;
        }

        if (to && to.length > 0) {

            const recipient: UserRecipientFieldType = {
                id: id,
                type: type,
                subType: type !== RecipientTypeEnum.Email ? RecipientSubTypeEnum.Cell : RecipientSubTypeEnum.None,
                to: type !== RecipientTypeEnum.Email ? `+1 ${to}` : to,
                disabled: false,
                disabledTimestamp: undefined,
                usageCount: 0,
                cloneRecipientID: undefined
            };

            if (!user.recipients.find(r => r.type === recipient.type &&
                r.subType === recipient.subType &&
                r.to === recipient.to)) {

                recipients.push(recipient);
            }
        }
    }

    function removeRecipient(recipients: UserRecipientFieldType[], to: string, type: RecipientTypeEnum) {


        const recipient = recipients.find(r => r.type === type && (r.type === RecipientTypeEnum.Email ? r.to : trimPhoneNumber(r.to)) === trimPhoneNumber(to));

        if (recipient) {
            recipients.splice(recipients.indexOf(recipient), 1);
        }

    }


    return (
        <>
            <LoadingZone isLoading={useUserStatus === "loading"}>
                <Form form={form} sx={{ padding: 0 }}>
                    <StepMenu
                        landing={landing}
                        steps={steps}
                        title={editMode ? "Edit User" : "New User"}
                        nonLinear={true}
                        activeStep={activeStep}
                        cancelButtonState={cancelButtonState}
                        prevButtonState={prevButtonState}
                        nextButtonState={nextButtonState}
                        saveAsNewButtonState={saveAsNewButtonState}
                        saveChangesButtonState={saveChangesButtonState}
                        onCancel={onCancelEdit}
                        onSaveAsNew={handleSubmit(() => onSaveAsNew())}
                        onSaveChanges={handleSubmit(() => onSaveChanges())}
                        onCancelStep={onCancelStep}
                        onStepChange={onStepChange}
                        onStepClick={step => onStepChange(activeStep, step.key)}
                    >
                        {activeStep === StepEnum.General &&
                            <GeneralStep />
                        }

                        {activeStep === StepEnum.AlarmNotifications &&
                            <AlarmNotificationsStep organizationID={organizationID} />
                        }

                        {activeStep === StepEnum.Membership &&
                            <MembershipStep organizationID={organizationID} />
                        }

                        {activeStep === StepEnum.Security &&
                            <SecurityStep organizationID={organizationID} />
                        }

                        {navigationV2 && activeStep === StepEnum.PageVisibility &&
                            <PageVisibilityStep organizationID={organizationID} />
                        }
                    </StepMenu>
                </Form>
            </LoadingZone >

            <SaveAsDialog {...bindSaveAsDialog(saveAsDialog)} />
            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserExitConfirmation}
            />
        </>
    );
}